import {
  ChevronLeft,
  EmailOutlined,
  IosShareOutlined,
  NoteAltOutlined,
  CheckCircleOutline,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  HorizontalRule,
  ErrorOutline,
  WarningAmber,
} from '@mui/icons-material'
import {
  Button,
  Container,
  Grid,
  Link as LinkMui,
  TableCell,
  TableHead,
  Table,
  Checkbox,
  TableRow,
  TableContainer,
  TableBody,
  ButtonGroup,
  Divider,
  Drawer,
  Typography,
  Stack,
  Tooltip,
  TableSortLabel,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Skeleton,
  Alert,
  AlertTitle,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useState, useContext, useMemo } from 'react'
import { useParams, Link as LinkRouter, useNavigate } from 'react-router-dom'
import {
  salesOrderType,
  salesOrderLineType,
  trackingType,
  invoiceType,
  purchaseOrderType,
  receivePaymentType,
  customerCreditType,
  rmaType,
  serialNumberType,
} from '../types'
import './SalesOrder.css'
import { DatePicker, Expandable, getRepCodeName, formatAddress, OpsOwner } from '../helper'
import dayjs from 'dayjs'
import CreateEmail from './CreateEmail'
import { dataContext } from '..'
import SalesOrderNotes from './SalesOrderNotes'
import SerialNumbers from './SerialNumbers'
import TrackingNumbers from './TrackingNumbers'
import EditLines from './EditLines'
import ErrorScreen from '../Misc/ErrorScreen'

// Local type extension for ETA editing state
type salesOrderLineWithEditing = salesOrderLineType & {
  eta_editing?: boolean
  originalEtaDate?: string | null
}

const CONDENSED_COLUMN_WIDTHS = {
  checkbox: '42px',
  number: '40px',
  itemName: '8%',
  itemDescription: undefined,
  vendor: '8%',
  qty: '8%',
  tracking: '8%',
  rate: '8%',
  serial: '8%',
  newEta: '8%',
  manuallyClosed: '85px',
  lastModified: '85px',
  complete: '40px',
}

const EXPANDED_COLUMN_WIDTHS = {
  checkbox: '24px',
  expanded: '36px',
  number: '40px',
  itemName: '6%',
  itemDescription: undefined,
  vendor: '5%',
  invoice: '4%',
  amount: '5%',
  qty: '5%',
  tracking: '6%',
  shipDate: '5%',
  carrier: '4%',
  deliveryDate: '5%',
  rate: '5%',
  serial: '6%',
  shipAddress: '5%',
  newEta: '6%',
  manuallyClosed: '85px',
  lastModified: '85px',
  complete: '40px',
}

function SalesOrder() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { operations, user, salesReps } = useContext(dataContext)

  const [loadError, setLoadError] = useState<'ok' | 'error' | 'unauthorized'>('ok')
  const [exportError, setExportError] = useState(false)
  const [opsOwnerError, setOpsOwnerError] = useState(false)
  const [salesOrder, setSalesOrder] = useState<
    | (salesOrderType & {
        sales_rep_phone: string
        sales_rep_email: string
        sales_rep_display_name: string
        parent_name: string
        customer_name: string
        display_name: string
        operations_owner_email: string
      })
    | null
  >(null)
  const [salesRepEmails, setSalesRepEmails] = useState<string[]>([])
  const [salesOrderLines, setSalesOrderLines] = useState<salesOrderLineWithEditing[]>([])
  const [trackingNumbers, setTrackingNumbers] = useState<trackingType[]>([])
  const [purchaseOrders, setPurchaseOrders] = useState<purchaseOrderType[]>([])
  const [invoices, setInvoices] = useState<invoiceType[]>([])
  const [customerCredits, setCustomerCredits] = useState<customerCreditType[]>([])
  const [receivePayments, setReceivePayments] = useState<receivePaymentType[]>([])
  const [rmas, setRmas] = useState<(rmaType & { rma_status: string })[]>([])
  const [tableTab, setTableTab] = useState('all')
  const [status, setStatus] = useState('')
  const [selectedLines, setSelectedLines] = useState<salesOrderLineWithEditing[]>([])
  const [emailOpen, setEmailOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [serialNumbersOpen, setSerialNumbersOpen] = useState(false)
  const [itemSelected, setItemSelected] = useState<{ id: string; name: string }>({ id: '', name: '' })
  const [serialNumbers, setSerialNumbers] = useState<serialNumberType[]>([])
  const [trackingNumbersOpen, setTrackingNumbersOpen] = useState(false)
  const [editLinesOpen, setEditLinesOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({})
  const [stickyHeader, setStickyHeader] = useState(true)
  const [condensedView, setCondensedView] = useState(user.user_type !== 'Operation Staff')
  const [orderBy, setOrderBy] = useState<string>('line_numerical_order')
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const compare = (a: Date | number | string | boolean | null, b: Date | number | string | boolean | null) => {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
    if (typeof a === 'number' && typeof b === 'number') return a - b
    return String(a).localeCompare(String(b))
  }

  const sortedLines = useMemo(() => {
    // First, filter the lines based on the tab
    let filtered: salesOrderLineWithEditing[]
    switch (tableTab) {
      case 'complete':
        filtered = salesOrderLines.filter(line => line.hide_flag)
        break
      case 'incomplete':
        filtered = salesOrderLines.filter(line => !line.hide_flag)
        break
      default:
        filtered = salesOrderLines
    }

    // Apply sorting
    return [...filtered].sort((a: salesOrderLineWithEditing, b: salesOrderLineWithEditing) => {
      const [sort, dir] = [orderBy, order]
      const aValue = a[sort as keyof salesOrderLineType]
      const bValue = b[sort as keyof salesOrderLineType]
      const cmp = compare(aValue, bValue)
      return dir === 'asc' ? cmp : -cmp
    })
  }, [salesOrderLines, tableTab, orderBy, order, trackingNumbers, serialNumbers])

  useEffect(() => {
    document.title = `Sales Order ${salesOrder?.ref_number ? `| ${salesOrder.ref_number}` : ''} | Salestrack`
  }, [salesOrder])

  useEffect(() => {
    if (user.user_type === 'Operation Staff') {
      setCondensedView(false)
    }
  }, [user])

  useEffect(() => {
    if (!user.active_flag) return

    setLoadError('ok')
    axios
      .get(`/salesOrder/${id}${user.user_type === 'Sales Rep' ? `?salesUser=${user.user_id}` : ''}`)
      .then(res => {
        setSalesOrder(res.data.salesOrder)
        setSalesRepEmails(res.data.salesRepEmails)
        setTrackingNumbers(res.data.trackingNumbers)
        setSerialNumbers(res.data.serialNumbers)
        setSalesOrderLines(res.data.salesOrderLines)
        setPurchaseOrders(res.data.purchaseOrders)
        setInvoices(res.data.invoices)
        setCustomerCredits(res.data.customerCredits)
        setReceivePayments(res.data.payments)
        setRmas(res.data.rmas)
        setStatus(res.data.statuses.join(', '))

        // Initialize expanded state
        handleExpand(res.data.salesOrderLines)
        handleCollapse()
      })
      .catch(err => {
        console.error(err)
        if (err.response?.status === 401) {
          setLoadError('unauthorized')
        } else {
          setLoadError('error')
        }
      })
  }, [id, user])

  const editLines = (updates: Partial<salesOrderLineWithEditing>[]) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .put(`/salesOrder/${id}/editLines`, { updates, userId: user.user_id })
        .then(res => {
          const updatedLines = res.data
          setSalesOrderLines(prevLines => {
            return prevLines.map(line => {
              const updatedLine = updatedLines.find(
                (updated: salesOrderLineWithEditing) =>
                  updated.sales_order_line_txn_id === line.sales_order_line_txn_id,
              )
              return updatedLine ? updatedLine : line
            })
          })
          setSelectedLines(prevSelectedLines => {
            return prevSelectedLines.map(line => {
              const updatedLine = updatedLines.find(
                (updated: salesOrderLineWithEditing) =>
                  updated.sales_order_line_txn_id === line.sales_order_line_txn_id,
              )
              return updatedLine ? updatedLine : line
            })
          })
        })
        .catch(err => {
          // TODO: Error UI
          console.error(err)
          reject(err)
        })
        .finally(() => {
          resolve()
        })
    })
  }

  const handleExport = () => {
    setExporting(true)
    axios
      .get(`/export/salesOrder/${salesOrder?.sales_order_txn_id}?email=${user.email}`, {
        responseType: 'blob',
      })
      .then(res => {
        if (res.status === 202) {
          alert('Your export is taking some time to complete. You will receive an email when it is ready.')
          return
        }

        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `Sales_Orders_Line_${dayjs().format('MM-DD-YYYY')}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch(err => {
        console.error(err)
        setExportError(true)
      })
      .finally(() => {
        setExporting(false)
      })
  }

  const handleAddTrackingNumber = (lineId: string, newTrackingNumber: trackingType) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .post(`/salesOrder/${salesOrder?.sales_order_txn_id}/addTrackingNumber`, {
          trackingNumber: newTrackingNumber,
          lineId: lineId,
          userId: user.user_id,
        })
        .then(() => {
          // Add the new tracking number to the tracking numbers list
          setTrackingNumbers(prevTrackingNumbers => [
            ...prevTrackingNumbers,
            {
              ...newTrackingNumber,
              sales_order_txn_id: id ?? '',
              sales_order_line_txn_id: lineId,
            },
          ])

          // Update sales order lines using the dedicated function
          const lineTrackingNumbers = trackingNumbers.filter(t => t.sales_order_line_txn_id === lineId)
          const newTotalShippedQuantity =
            lineTrackingNumbers.reduce((sum, t) => sum + (t.quantity_shipped || 0), 0) +
            (newTrackingNumber.quantity_shipped || 0)

          handleSalesOrderLinesUpdate([
            {
              sales_order_line_txn_id: lineId,
              total_shipped_quantity: newTotalShippedQuantity,
              tracking_number_exist_flag: true,
            },
          ])

          resolve()
        })
        .catch(err => {
          // case - tracking number already exists
          if (err.response?.status === 400 && err.response?.data?.includes('Tracking number already exists')) {
            reject(new Error('Duplicate tracking number cannot be added.'))
          } else {
            console.error('Error adding tracking number:', err)
            reject(
              new Error(
                'An error occurred while adding the new tracking number. Please try again or contact support if the error persists.',
              ),
            )
          }
        })
    })
  }

  const handleEditTrackingNumbers = (
    lineId: string,
    editingTrackingNumber: (trackingType & { originalTrackingNumber: string })[],
  ) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .put(`/salesOrder/${lineId}/editTrackingNumber`, {
          trackingNumbers: editingTrackingNumber,
          userId: user.user_id,
          soNumber: salesOrder?.sales_order_txn_id,
        })
        .then(() => {
          setTrackingNumbers(prevTrackingNumbers =>
            prevTrackingNumbers.map(t => {
              const editedTrackingNumber = editingTrackingNumber.find(
                et => et.originalTrackingNumber === t.tracking_number && lineId === t.sales_order_line_txn_id,
              )
              if (editedTrackingNumber) {
                const { originalTrackingNumber: _, ...rest } = editedTrackingNumber
                return {
                  ...rest,
                  sales_order_line_txn_id: lineId,
                  editing: false,
                }
              }
              return t
            }),
          )

          const lineTrackingNumbers = trackingNumbers.filter(t => t.sales_order_line_txn_id === lineId)
          const newTotalShippedQuantity = lineTrackingNumbers.reduce((sum, t) => {
            const editedTracking = editingTrackingNumber.find(et => et.tracking_number === t.tracking_number)
            return sum + (editedTracking?.quantity_shipped || t.quantity_shipped || 0)
          }, 0)

          handleSalesOrderLinesUpdate([
            {
              sales_order_line_txn_id: lineId,
              total_shipped_quantity: newTotalShippedQuantity,
              tracking_number_exist_flag: lineTrackingNumbers.length > 0,
            },
          ])

          resolve()
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }

  const handleSalesOrderLinesUpdate = (updatedSalesOrderLines: Partial<salesOrderLineType>[]) => {
    setSalesOrderLines(prevLines => {
      return prevLines.map(line => {
        const updatedLine = updatedSalesOrderLines.find(
          updated => updated.sales_order_line_txn_id === line.sales_order_line_txn_id,
        )
        return updatedLine ? { ...line, ...updatedLine } : line
      })
    })
  }

  const handleExpand = (lines: salesOrderLineWithEditing[]) => {
    setExpanded(
      lines.reduce(
        (acc, line) => {
          acc[line.sales_order_line_txn_id] = true
          return acc
        },
        {} as { [key: string]: boolean },
      ),
    )
  }

  const handleCollapse = () => {
    setExpanded(prev => {
      const newExpanded = { ...prev }
      Object.keys(newExpanded).forEach(key => {
        newExpanded[key] = false
      })
      return newExpanded
    })
  }

  const handleEditOpsOwner = (salesOrderTxnId: string, operationsOwner: string | null) => {
    axios
      .post(`/salesOrder/${salesOrderTxnId}/updateOperationsOwner`, {
        operationsOwner,
        userId: user.user_id,
      })
      .then(() => {
        setSalesOrder(prev => {
          if (prev) {
            return {
              ...prev,
              operations_owner: operationsOwner,
              operations_owner_email: operationsOwner
                ? operations.find(operation => operation.user_id === operationsOwner)?.email ?? ''
                : '',
            }
          }
          return prev
        })
        setOpsOwnerError(false)
      })
      .catch(err => {
        console.error(err)
        setOpsOwnerError(true)
      })
  }

  if (loadError !== 'ok') {
    return (
      <div className="sales-order-detail">
        <div className="sales-order-detail-load-error-button-container">
          <Button size="large" variant="contained" startIcon={<ChevronLeft />} onClick={() => navigate(-1)}>
            Return
          </Button>
        </div>
        <ErrorScreen
          title="Sales Order Load Failed"
          errorText={
            loadError === 'unauthorized'
              ? 'You are not authorized to view this sales order.'
              : 'Failed to load sales order details. Please try again or contact support if the error persists.'
          }
        />
      </div>
    )
  }

  return (
    <div className="sales-order-detail">
      <div className="sales-order-detail-button-container">
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            size="large"
            variant="contained"
            startIcon={<ChevronLeft />}
            component={LinkRouter}
            to="/salesorder"
            loading={!salesOrder}
            onClick={e => {
              e.preventDefault()
              navigate(-1)
            }}
          >
            Return
          </Button>
          {!salesOrder ? (
            <>
              <Skeleton variant="rounded" width={180} height={32} />
              <Skeleton variant="rounded" width={180} height={32} />
            </>
          ) : (
            <>
              <Chip label={<Typography variant="h6">{`Sales Order #: ${salesOrder.ref_number}`}</Typography>} />
              <OpsOwner
                operationsOwner={salesOrder.operations_owner}
                objectId={salesOrder.sales_order_txn_id}
                handleEditOpsOwner={handleEditOpsOwner}
              />
              <Snackbar open={opsOwnerError} onClose={() => setOpsOwnerError(false)}>
                <Alert severity="error">
                  <AlertTitle>Error</AlertTitle>
                  Failed to update operations owner. Please try again or contact support if the error persists.
                </Alert>
              </Snackbar>
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            size="large"
            variant="contained"
            startIcon={<IosShareOutlined />}
            onClick={handleExport}
            loading={exporting || !salesOrder}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button
            size="large"
            variant="contained"
            startIcon={<NoteAltOutlined />}
            onClick={() => setNotesOpen(true)}
            loading={!salesOrder}
          >
            Notes
          </Button>
          <Drawer open={notesOpen} onClose={() => setNotesOpen(false)} anchor="right">
            <SalesOrderNotes
              salesOrderId={salesOrder?.sales_order_txn_id ?? null}
              objectType="salesOrder"
              setNotesOpen={setNotesOpen}
              noteDraft={noteDraft}
              setNoteDraft={setNoteDraft}
            />
          </Drawer>
          {user.user_type !== 'Office Staff' && (
            <Button
              size="large"
              variant="contained"
              startIcon={<EmailOutlined />}
              onClick={() => setEmailOpen(true)}
              loading={!salesOrder}
            >
              Create Email
            </Button>
          )}
          <Drawer open={emailOpen} onClose={() => setEmailOpen(false)} anchor="right">
            <CreateEmail
              order={salesOrder as salesOrderType}
              lineItems={sortedLines.filter(line => !(line.item_name === 'Delivery-Z' && line.quantity === 1))}
              trackingNumbers={trackingNumbers}
              salesRep={{
                rep_code: salesOrder?.sales_rep_display_name ?? '',
                sales_rep_phone: salesOrder?.sales_rep_phone ?? '',
                sales_rep_email: salesOrder?.sales_rep_email ?? '',
              }}
              supportEmails={salesRepEmails}
              isVIT={salesOrder?.master_contract_bpa_number === 'NNG15SD41B'}
              setOpen={setEmailOpen}
            />
          </Drawer>
        </Stack>
      </div>
      <div className="sales-order-detail-data-container">
        <div className="sales-order-detail-header-div">
          <Container className="sales-order-detail-header-container" maxWidth={false}>
            <Container className="sales-order-detail-header-column" maxWidth={false}>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Quote Number:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body1">{salesOrder.quote_works_number ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Customer / Job Name:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={200} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.parent_name} / {salesOrder.customer_name}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Total Amount:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.total_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Total Sales Tax:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.total_sales_tax?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    QB Last Update Date:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.last_update_date_qb
                      ? dayjs.utc(salesOrder.last_update_date_qb).format('MM/DD/YYYY')
                      : '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Order Status:
                  </Typography>
                )}
                {!salesOrder ? <Skeleton width={120} /> : <Typography variant="body1">{status}</Typography>}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Customer PO:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">{salesOrder.customer_po_number ?? '-'}</Typography>
                )}
              </Grid>
            </Container>
            <Container className="sales-order-detail-header-column" maxWidth={false}>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Sales Rep:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body1">
                    {getRepCodeName(salesReps.find(rep => rep.initial === salesOrder.rep_code))}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    DPAS:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">{salesOrder.dpass ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Transaction Date:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.txn_date ? dayjs.utc(salesOrder.txn_date).format('MM/DD/YYYY') : '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Due Date:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.due_date ? dayjs.utc(salesOrder.due_date).format('MM/DD/YYYY') : '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Last Update Date:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.last_update_date ? dayjs.utc(salesOrder.last_update_date).format('MM/DD/YYYY') : '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={150} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Master Contract/BPA#:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body1">{salesOrder.master_contract_bpa_number ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={100} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Gov Award #:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body1">{salesOrder.government_award_number ?? '-'}</Typography>
                )}
              </Grid>
            </Container>
            <Container className="sales-order-detail-header-column" maxWidth={false}>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ship Address Attention:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={150} />
                ) : (
                  <Typography variant="body1">{salesOrder.ship_address_attention ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ship Address Addressee:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={150} />
                ) : (
                  <Typography variant="body1">{salesOrder.ship_address_addressee ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ship Address 1:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={150} />
                ) : (
                  <Typography variant="body1">{salesOrder.ship_address_addr1 ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ship Address 2:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={150} />
                ) : (
                  <Typography variant="body1">{salesOrder.ship_address_addr2 ?? '-'}</Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={140} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ship City, State & Zip Code:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={150} />
                ) : (
                  <Typography variant="body1">
                    {salesOrder.ship_address_city && salesOrder.ship_address_state && salesOrder.ship_address_zip
                      ? `${salesOrder.ship_address_city}, ${salesOrder.ship_address_state} ${salesOrder.ship_address_zip}`
                      : '-'}
                  </Typography>
                )}
              </Grid>
              <Grid className="sales-order-detail-header-grid">
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Last Update User:
                  </Typography>
                )}
                {!salesOrder ? (
                  <Skeleton width={120} />
                ) : (
                  <Typography variant="body1">{salesOrder.display_name}</Typography>
                )}
              </Grid>
            </Container>
          </Container>
        </div>

        <TableContainer className="sales-order-detail-table-container">
          <Accordion
            slotProps={{
              transition: {
                onEntered: () => {
                  setStickyHeader(true)
                },
                onExit: () => {
                  setStickyHeader(false)
                },
              },
            }}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ExpandMore className="MuiAccordionSummary-expandIcon" />} component="div">
              <FormControlLabel
                control={
                  <Switch
                    checked={condensedView}
                    onChange={e => {
                      setCondensedView(e.target.checked)
                      handleCollapse()
                    }}
                    size="small"
                  />
                }
                onClick={e => e.stopPropagation()}
                label="Condensed View"
              />
              <Tabs value={tableTab} onChange={(_, value) => setTableTab(value)} onClick={e => e.stopPropagation()}>
                <Tab label={`All (${salesOrderLines.length})`} value="all" />
                <Tab
                  label={`Incomplete (${
                    salesOrderLines.length - salesOrderLines.filter(line => line.hide_flag).length
                  })`}
                  value="incomplete"
                />
                <Tab label={`Complete (${salesOrderLines.filter(line => line.hide_flag).length})`} value="complete" />
              </Tabs>
              {!salesOrder ? (
                <Skeleton width={80} />
              ) : (
                <Typography color="text.secondary">{salesOrderLines.length} Items</Typography>
              )}
            </AccordionSummary>
            <AccordionDetails>
              {user.user_type !== 'Office Staff' && (
                <div className={stickyHeader ? 'sales-order-detail-table-pagination-line-sticky' : undefined}>
                  <Divider />
                  <div className="sales-order-detail-table-pagination-line">
                    {!salesOrder ? (
                      <Skeleton width={120} />
                    ) : (
                      <Typography color="text.secondary" className="selected-count">
                        {selectedLines.length} Selected
                      </Typography>
                    )}
                    <ButtonGroup>
                      <Button
                        onClick={() => {
                          if (selectedLines.length > 0) {
                            navigate('/rma/create', { state: { salesOrder: salesOrder, lineItems: selectedLines } })
                          }
                        }}
                        loading={!salesOrder}
                      >
                        Create RMA
                      </Button>
                      <Button onClick={() => setEditLinesOpen(true)} loading={!salesOrder}>
                        Edit
                      </Button>
                      {(user.user_type === 'Administrator' || user.user_type === 'Operation Staff') && (
                        <Button
                          onClick={() =>
                            editLines(
                              selectedLines.map(line => ({
                                sales_order_txn_id: salesOrder?.sales_order_txn_id,
                                sales_order_line_txn_id: line.sales_order_line_txn_id,
                                hide_flag: true,
                              })),
                            )
                          }
                          loading={!salesOrder}
                        >
                          Mark as Completed
                        </Button>
                      )}
                    </ButtonGroup>
                  </div>
                </div>
              )}
              {!condensedView ? (
                <Table className="sales-order-detail-table-expanded" color="primary" stickyHeader={stickyHeader}>
                  <TableHead
                    className="sales-order-detail-table-header"
                    sx={{
                      '& .MuiTableCell-stickyHeader': {
                        top: user.user_type === 'Office Staff' ? '176.25px' : '228.75px',
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.checkbox}>
                        {!salesOrder ? (
                          <Skeleton width={20} />
                        ) : (
                          <Checkbox
                            checked={selectedLines.length === sortedLines.length}
                            indeterminate={selectedLines.length > 0 && selectedLines.length < sortedLines.length}
                            onChange={(_, checked) => {
                              if (checked) {
                                setSelectedLines(sortedLines)
                              } else {
                                setSelectedLines([])
                              }
                            }}
                            disabled={editLinesOpen}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        onClick={() => {
                          Object.values(expanded).some(value => value) ? handleCollapse() : handleExpand(sortedLines)
                        }}
                        width={EXPANDED_COLUMN_WIDTHS.expanded}
                      >
                        {!salesOrder ? (
                          <Skeleton width={20} />
                        ) : (
                          <IconButton>
                            {(() => {
                              const expandedLines = sortedLines.filter(line => expanded[line.sales_order_line_txn_id])
                              if (expandedLines.length === 0) {
                                return <ExpandMore />
                              } else if (expandedLines.length === sortedLines.length) {
                                return <ExpandLess />
                              } else {
                                return <HorizontalRule />
                              }
                            })()}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.number}>
                        {!salesOrder ? (
                          <Skeleton width={40} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'line_numerical_order'}
                            direction={orderBy === 'line_numerical_order' ? order : 'asc'}
                            onClick={() => handleRequestSort('line_numerical_order')}
                          >
                            #
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.itemName}>
                        {!salesOrder ? (
                          <Skeleton width={120} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'item_name'}
                            direction={orderBy === 'item_name' ? order : 'asc'}
                            onClick={() => handleRequestSort('item_name')}
                          >
                            Item #<br />
                            (Mfr Part #)
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.itemDescription}>
                        {!salesOrder ? (
                          <Skeleton width={150} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'item_desc'}
                            direction={orderBy === 'item_desc' ? order : 'asc'}
                            onClick={() => handleRequestSort('item_desc')}
                          >
                            Item Description
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.vendor}>
                        {!salesOrder ? (
                          <Skeleton width={120} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'vendor_name'}
                            direction={orderBy === 'vendor_name' ? order : 'asc'}
                            onClick={() => handleRequestSort('vendor_name')}
                          >
                            Partner / Vendor PO
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.invoice}>
                        {!salesOrder ? (
                          <Skeleton width={40} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'invoice'}
                            direction={orderBy === 'invoice' ? order : 'asc'}
                            onClick={() => handleRequestSort('invoice')}
                          >
                            Invoice
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.qty} align="right">
                        {!salesOrder ? (
                          <Skeleton width={40} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'quantity'}
                            direction={orderBy === 'quantity' ? order : 'asc'}
                            onClick={() => handleRequestSort('quantity')}
                          >
                            Qty
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.amount} align="right">
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'amount'}
                            direction={orderBy === 'amount' ? order : 'asc'}
                            onClick={() => handleRequestSort('amount')}
                          >
                            Amount / Unit Price
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.serial} align="right">
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'serial.serial_number'}
                            direction={orderBy === 'serial.serial_number' ? order : 'asc'}
                            onClick={() => handleRequestSort('serial.serial_number')}
                          >
                            Serial #
                          </TableSortLabel>
                        )}
                      </TableCell>
                      {salesOrder && !salesOrder.ship_address_addr1 && (
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.shipAddress}>Ship to Address</TableCell>
                      )}
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.tracking} align="right">
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'tracking.tracking_number'}
                            direction={orderBy === 'tracking.tracking_number' ? order : 'asc'}
                            onClick={() => handleRequestSort('tracking.tracking_number')}
                          >
                            Shipping Information
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.shipDate} align="right">
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'tracking.ship_date'}
                            direction={orderBy === 'tracking.ship_date' ? order : 'asc'}
                            onClick={() => handleRequestSort('tracking.ship_date')}
                          >
                            Ship Date
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.carrier} align="right">
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'tracking.ship_method'}
                            direction={orderBy === 'tracking.ship_method' ? order : 'asc'}
                            onClick={() => handleRequestSort('tracking.ship_method')}
                          >
                            Carrier
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell
                        onClick={() => handleRequestSort('tracking.delivery_date')}
                        width={EXPANDED_COLUMN_WIDTHS.deliveryDate}
                        align="right"
                      >
                        {!salesOrder ? (
                          <Skeleton width={80} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'tracking.delivery_date'}
                            direction={orderBy === 'tracking.delivery_date' ? order : 'asc'}
                            onClick={() => handleRequestSort('tracking.delivery_date')}
                          >
                            Delivery Date
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.newEta}>
                        {!salesOrder ? (
                          <Skeleton width={80} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'eta_date_current'}
                            direction={orderBy === 'eta_date_current' ? order : 'asc'}
                            onClick={() => handleRequestSort('eta_date_current')}
                          >
                            ETA Date
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.manuallyClosed}>
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'is_manually_closed'}
                            direction={orderBy === 'is_manually_closed' ? order : 'asc'}
                            onClick={() => handleRequestSort('is_manually_closed')}
                          >
                            Manually Closed
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.lastModified}>
                        {!salesOrder ? (
                          <Skeleton width={60} />
                        ) : (
                          <TableSortLabel
                            active={orderBy === 'last_update_date'}
                            direction={orderBy === 'last_update_date' ? order : 'asc'}
                            onClick={() => handleRequestSort('last_update_date')}
                          >
                            Last Modified
                          </TableSortLabel>
                        )}
                      </TableCell>
                      <TableCell width={EXPANDED_COLUMN_WIDTHS.complete} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLines.map(line => (
                      <TableRow>
                        <TableCell className="sales-order-detail-icon-cell" width={EXPANDED_COLUMN_WIDTHS.checkbox}>
                          <Checkbox
                            checked={
                              selectedLines.filter(l => l.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                .length > 0
                            }
                            onChange={(_, checked) => {
                              if (checked) {
                                setSelectedLines([...selectedLines, line])
                              } else {
                                setSelectedLines(selectedLines.filter(l => l !== line))
                              }
                            }}
                            disabled={editLinesOpen}
                          />
                        </TableCell>
                        <TableCell className="sales-order-detail-icon-cell" width={EXPANDED_COLUMN_WIDTHS.expanded}>
                          <IconButton
                            onClick={() =>
                              setExpanded({
                                ...expanded,
                                [line.sales_order_line_txn_id]: !expanded[line.sales_order_line_txn_id],
                              })
                            }
                          >
                            {expanded[line.sales_order_line_txn_id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.number}>{line.line_numerical_order}</TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.itemName}>
                          <div className="sales-order-detail-container-cell">
                            {line.item_name ?? '-'}
                            {line.late_flag && <Chip size="small" color="error" label="Late" icon={<ErrorOutline />} />}
                          </div>
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.itemDescription}>
                          <Expandable
                            key={`${line.sales_order_line_txn_id}-condensed`}
                            text={line.item_desc ?? '-'}
                            maxHeight={7}
                          />
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.vendor}>{line.vendor_name ?? '-'}</TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.invoice}>{'-'}</TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.qty}>
                          <div className="sales-order-detail-container-cell">
                            <Typography variant="body2">Ordered: {line.quantity ?? '-'}</Typography>
                            <Typography variant="body2">
                              Shipped: {(line.total_shipped_quantity ?? 0) > 0 ? line.total_shipped_quantity : '-'}
                            </Typography>
                            <Typography variant="body2">
                              RMA: {(line.rma_count ?? 0) > 0 ? line.rma_count : '-'}
                            </Typography>
                            {line.quantity !== null && line.quantity < (line.total_shipped_quantity ?? 0) && (
                              <Chip size="small" color="error" label="Overshipped" icon={<ErrorOutline />} />
                            )}
                            {line.quantity === 0 && (
                              <Chip size="small" color="warning" label="Zero Qty" icon={<WarningAmber />} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.amount}>
                          <div className="sales-order-detail-container-cell">
                            <Typography variant="body2">
                              Amount:{' '}
                              {line.amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                            </Typography>
                            <Typography variant="body2">
                              Unit Price:{' '}
                              {line.rate?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.serial}>
                          <div className="sales-order-detail-container-cell right-align">
                            <Expandable
                              text={serialNumbers
                                .filter(s => s.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                .map(s => s.serial_number)
                                .sort()
                                .join('\n')}
                              maxHeight={expanded[line.sales_order_line_txn_id] ? -1 : 8}
                              hideExpandable
                              gap={8}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSerialNumbersOpen(true)
                                setItemSelected({ id: line.sales_order_line_txn_id, name: line.item_name ?? '' })
                              }}
                            >
                              View All (
                              {
                                serialNumbers.filter(s => s.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                  .length
                              }
                              )
                            </Button>
                          </div>
                        </TableCell>
                        {salesOrder && !salesOrder.ship_address_addr1 && (
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              {formatAddress(line) || '-'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.tracking}>
                          <div className="sales-order-detail-container-cell right-align">
                            <Expandable
                              text={trackingNumbers
                                .filter(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                .sort()
                                .map(t => t.tracking_number)
                                .join('\n')}
                              gap={8}
                              maxHeight={expanded[line.sales_order_line_txn_id] ? -1 : 8}
                              hideExpandable
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setTrackingNumbersOpen(true)
                                setItemSelected({ id: line.sales_order_line_txn_id, name: line.item_name ?? '' })
                              }}
                            >
                              View All (
                              {
                                trackingNumbers.filter(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                  .length
                              }
                              )
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.shipDate}>
                          <Expandable
                            text={trackingNumbers
                              .filter(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                              .sort()
                              .map(t => (t.ship_date ? dayjs.utc(t.ship_date).format('MM/DD/YYYY') : '-'))
                              .join('\n')}
                            maxHeight={expanded[line.sales_order_line_txn_id] ? -1 : 8}
                            hideExpandable
                            gap={8}
                          />
                        </TableCell>
                        <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.carrier}>
                          <Expandable
                            text={trackingNumbers
                              .filter(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                              .sort()
                              .map(t => (t.ship_method ? t.ship_method : '-'))
                              .join('\n')}
                            maxHeight={expanded[line.sales_order_line_txn_id] ? -1 : 8}
                            hideExpandable
                            gap={8}
                          />
                        </TableCell>
                        <TableCell align="right" width={EXPANDED_COLUMN_WIDTHS.deliveryDate}>
                          <Expandable
                            text={trackingNumbers
                              .filter(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                              .sort()
                              .map(t => (t.delivery_date ? dayjs.utc(t.delivery_date).format('MM/DD/YYYY') : '-'))
                              .join('\n')}
                            maxHeight={expanded[line.sales_order_line_txn_id] ? -1 : 8}
                            hideExpandable
                            gap={8}
                          />
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.newEta} align="right">
                          <div className="sales-order-detail-eta-cell-container">
                            {user.user_type === 'Office Staff' ? (
                              <Typography variant="body2" color="text.primary">
                                Current ETA Date:{' '}
                                {line.eta_date_current ? dayjs.utc(line.eta_date_current).format('MM/DD/YYYY') : '-'}
                              </Typography>
                            ) : (
                              <>
                                {line.eta_editing ? (
                                  <>
                                    <DatePicker
                                      label="New ETA Date"
                                      size="small"
                                      value={line.eta_date_current ? dayjs.utc(line.eta_date_current) : undefined}
                                      onChange={e => {
                                        line.eta_date_current = e?.format('YYYY-MM-DD') ?? ''
                                      }}
                                    />
                                    <Button
                                      onClick={e => {
                                        e.preventDefault()
                                        // Compare with the original date stored when editing started
                                        const originalDate = line.originalEtaDate
                                        const currentDate = line.eta_date_current

                                        // Only update if the date actually changed
                                        if (originalDate !== currentDate) {
                                          editLines([
                                            {
                                              sales_order_txn_id: salesOrder?.sales_order_txn_id ?? '',
                                              sales_order_line_txn_id: line.sales_order_line_txn_id,
                                              eta_date_current: line.eta_date_current,
                                              eta_date_previous: originalDate ?? null,
                                            },
                                          ]).then(() => {
                                            // Reset editing state after successful update
                                            setSalesOrderLines(prevLines =>
                                              prevLines.map(l =>
                                                l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                                  ? {
                                                      ...l,
                                                      eta_editing: false,
                                                      originalEtaDate: undefined,
                                                      eta_date_current: currentDate,
                                                      eta_date_previous: originalDate ?? null,
                                                    }
                                                  : l,
                                              ),
                                            )
                                          })
                                        } else {
                                          // Just reset editing state if no change
                                          setSalesOrderLines(prevLines =>
                                            prevLines.map(l =>
                                              l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                                ? { ...l, eta_editing: false, originalEtaDate: undefined }
                                                : l,
                                            ),
                                          )
                                        }
                                      }}
                                    >
                                      Save
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="body2" color="text.primary">
                                      New ETA Date:{' '}
                                      {line.eta_date_current
                                        ? dayjs.utc(line.eta_date_current).format('MM/DD/YYYY')
                                        : '-'}
                                    </Typography>
                                    <Button
                                      onClick={e => {
                                        e.preventDefault()
                                        setSalesOrderLines(prevLines =>
                                          prevLines.map(l =>
                                            l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                              ? { ...l, eta_editing: true, originalEtaDate: l.eta_date_current }
                                              : l,
                                          ),
                                        )
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                            <Divider />
                            <Typography variant="body2" color="text.primary">
                              Old ETA Date:{' '}
                              {line.eta_date_previous ? dayjs.utc(line.eta_date_previous).format('MM/DD/YYYY') : '-'}
                            </Typography>
                            {user.user_type !== 'Office Staff' && (
                              <Button
                                onClick={e => {
                                  e.preventDefault()
                                  editLines([
                                    {
                                      sales_order_txn_id: salesOrder?.sales_order_txn_id ?? '',
                                      sales_order_line_txn_id: line.sales_order_line_txn_id,
                                      eta_date_previous: null,
                                      eta_date_current: null,
                                    },
                                  ])
                                }}
                              >
                                Clear All
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.manuallyClosed}>
                          {line.is_manually_closed ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell width={EXPANDED_COLUMN_WIDTHS.lastModified}>
                          {line.last_update_date ? dayjs.utc(line.last_update_date).format('MM/DD/YYYY') : '-'}
                        </TableCell>
                        <TableCell className="sales-order-detail-icon-cell" width={EXPANDED_COLUMN_WIDTHS.complete}>
                          <Tooltip
                            title={line.hide_flag ? 'Mark as not completed' : 'Mark as completed'}
                            placement="left"
                          >
                            <IconButton
                              onClick={() => {
                                if (user.user_type === 'Office Staff') {
                                  return
                                }
                                editLines([
                                  {
                                    sales_order_txn_id: salesOrder?.sales_order_txn_id ?? '',
                                    sales_order_line_txn_id: line.sales_order_line_txn_id,
                                    hide_flag: !line.hide_flag,
                                  },
                                ])
                              }}
                            >
                              {line.hide_flag ? <CheckCircle color="success" /> : <CheckCircleOutline />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table color="primary" stickyHeader={stickyHeader}>
                  <TableHead
                    className={`sales-order-detail-table-header`}
                    sx={{
                      '& .MuiTableCell-stickyHeader': {
                        top: user.user_type === 'Office Staff' ? '176.25px !important' : '228.75px !important',
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.checkbox}>
                        <Checkbox
                          checked={selectedLines.length === sortedLines.length}
                          indeterminate={selectedLines.length > 0 && selectedLines.length < sortedLines.length}
                          onChange={(_, checked) => {
                            if (checked) {
                              setSelectedLines(sortedLines)
                            } else {
                              setSelectedLines([])
                            }
                          }}
                          disabled={editLinesOpen}
                        />
                      </TableCell>
                      <TableCell
                        onClick={() => handleRequestSort('line_numerical_order')}
                        width={CONDENSED_COLUMN_WIDTHS.number}
                      >
                        <TableSortLabel
                          active={orderBy === 'line_numerical_order'}
                          direction={orderBy === 'line_numerical_order' ? order : 'asc'}
                          onClick={() => handleRequestSort('line_numerical_order')}
                        >
                          #
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.itemName}>
                        <TableSortLabel
                          active={orderBy === 'item_name'}
                          direction={orderBy === 'item_name' ? order : 'asc'}
                          onClick={() => handleRequestSort('item_name')}
                        >
                          Item #<br />
                          (Mfr Part #)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.itemDescription}>
                        <TableSortLabel
                          active={orderBy === 'item_desc'}
                          direction={orderBy === 'item_desc' ? order : 'asc'}
                          onClick={() => handleRequestSort('item_desc')}
                        >
                          Item Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.vendor}>
                        <TableSortLabel
                          active={orderBy === 'vendor_name'}
                          direction={orderBy === 'vendor_name' ? order : 'asc'}
                          onClick={() => handleRequestSort('vendor_name')}
                        >
                          Partner /<br />
                          Vendor PO
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.qty} align="right">
                        <TableSortLabel
                          active={orderBy === 'quantity'}
                          direction={orderBy === 'quantity' ? order : 'asc'}
                          onClick={() => handleRequestSort('quantity')}
                        >
                          Qty
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" width={CONDENSED_COLUMN_WIDTHS.rate}>
                        <TableSortLabel
                          active={orderBy === 'amount'}
                          direction={orderBy === 'amount' ? order : 'asc'}
                          onClick={() => handleRequestSort('amount')}
                        >
                          Amount / Unit Price
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.serial}>
                        <TableSortLabel
                          active={orderBy === 'serial.serial_number'}
                          direction={orderBy === 'serial.serial_number' ? order : 'asc'}
                          onClick={() => handleRequestSort('serial.serial_number')}
                        >
                          Serial #
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.tracking}>
                        <TableSortLabel
                          active={orderBy === 'tracking.tracking_number'}
                          direction={orderBy === 'tracking.tracking_number' ? order : 'asc'}
                          onClick={() => handleRequestSort('tracking.tracking_number')}
                        >
                          Shipping Information
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.newEta} align="right">
                        <TableSortLabel
                          active={orderBy === 'eta_date_current'}
                          direction={orderBy === 'eta_date_current' ? order : 'asc'}
                          onClick={() => handleRequestSort('eta_date_current')}
                        >
                          ETA Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.manuallyClosed}>
                        <TableSortLabel
                          active={orderBy === 'is_manually_closed'}
                          direction={orderBy === 'is_manually_closed' ? order : 'asc'}
                          onClick={() => handleRequestSort('is_manually_closed')}
                        >
                          Manually Closed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.lastModified} align="right">
                        <TableSortLabel
                          active={orderBy === 'last_update_date'}
                          direction={orderBy === 'last_update_date' ? order : 'asc'}
                          onClick={() => handleRequestSort('last_update_date')}
                        >
                          Last Modified
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={CONDENSED_COLUMN_WIDTHS.complete} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLines.map(line => (
                      <TableRow>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.checkbox}>
                          <Checkbox
                            checked={
                              selectedLines.filter(l => l.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                .length > 0
                            }
                            onChange={(_, checked) => {
                              if (checked) {
                                setSelectedLines([...selectedLines, line])
                              } else {
                                setSelectedLines(selectedLines.filter(l => l !== line))
                              }
                            }}
                            disabled={editLinesOpen}
                          />
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.number}>{line.line_numerical_order}</TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.itemName}>
                          <div>
                            {line.item_name ?? '-'}
                            {line.late_flag && <Chip size="small" color="error" label="Late" icon={<ErrorOutline />} />}
                          </div>
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.itemDescription}>
                          <Expandable
                            key={`${line.sales_order_line_txn_id}-expanded`}
                            text={line.item_desc ?? '-'}
                            maxHeight={5}
                          />
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.vendor}>{line.vendor_name ?? '-'}</TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.qty} align="right">
                          <div className="sales-order-detail-container-cell right-align">
                            <Typography variant="body2">Ordered: {line.quantity ?? '-'}</Typography>
                            <Typography variant="body2">
                              Shipped: {(line.total_shipped_quantity ?? 0) > 0 ? line.total_shipped_quantity : '-'}
                            </Typography>
                            <Typography variant="body2">
                              RMA: {(line.rma_count ?? 0) > 0 ? line.rma_count : '-'}
                            </Typography>
                            {line.quantity !== null && line.quantity < (line.total_shipped_quantity ?? 0) && (
                              <Chip size="small" color="error" label="Overshipped" icon={<ErrorOutline />} />
                            )}
                            {line.quantity === 0 && (
                              <Chip size="small" color="warning" label="Zero Qty" icon={<WarningAmber />} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.rate} align="right">
                          <div className="sales-order-detail-container-cell">
                            <Typography variant="body2">
                              Amount:{' '}
                              {line.amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                            </Typography>
                            <Typography variant="body2">
                              Unit Price:{' '}
                              {line.rate?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.serial}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSerialNumbersOpen(true)
                              setItemSelected({ id: line.sales_order_line_txn_id, name: line.item_name ?? '' })
                            }}
                          >
                            View All (
                            {
                              serialNumbers.filter(s => s.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                .length
                            }
                            )
                          </Button>
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.tracking}>
                          <div className="sales-order-detail-container-cell">
                            <Typography variant="body2">
                              Tracking #:{' '}
                              {trackingNumbers.find(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                ?.tracking_number ?? '-'}
                            </Typography>
                            <Typography variant="body2">
                              Ship Date:{' '}
                              {trackingNumbers.find(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                ?.ship_date
                                ? dayjs
                                    .utc(
                                      trackingNumbers.find(
                                        t => t.sales_order_line_txn_id === line.sales_order_line_txn_id,
                                      )?.ship_date,
                                    )
                                    .format('MM/DD/YYYY')
                                : '-'}
                            </Typography>
                            <Typography variant="body2">
                              Carrier:{' '}
                              {trackingNumbers.find(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                ?.ship_method ?? '-'}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setTrackingNumbersOpen(true)
                                setItemSelected({ id: line.sales_order_line_txn_id, name: line.item_name ?? '' })
                              }}
                            >
                              View All (
                              {
                                trackingNumbers.filter(t => t.sales_order_line_txn_id === line.sales_order_line_txn_id)
                                  .length
                              }
                              )
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell align="right" width={CONDENSED_COLUMN_WIDTHS.newEta}>
                          <div className="sales-order-detail-eta-cell-container">
                            {user.user_type === 'Office Staff' ? (
                              <Typography variant="body2" color="text.primary">
                                Current ETA Date:{' '}
                                {line.eta_date_current ? dayjs.utc(line.eta_date_current).format('MM/DD/YYYY') : '-'}
                              </Typography>
                            ) : (
                              <>
                                {line.eta_editing ? (
                                  <>
                                    <DatePicker
                                      label="New ETA Date"
                                      size="small"
                                      value={line.eta_date_current ? dayjs.utc(line.eta_date_current) : undefined}
                                      onChange={e => {
                                        line.eta_date_current = e?.format('YYYY-MM-DD') ?? ''
                                      }}
                                    />
                                    <Button
                                      onClick={e => {
                                        e.preventDefault()
                                        // Compare with the original date stored when editing started
                                        const originalDate = line.originalEtaDate
                                        const currentDate = line.eta_date_current

                                        // Only update if the date actually changed
                                        if (originalDate !== currentDate) {
                                          editLines([
                                            {
                                              sales_order_txn_id: salesOrder?.sales_order_txn_id ?? '',
                                              sales_order_line_txn_id: line.sales_order_line_txn_id,
                                              eta_date_current: line.eta_date_current,
                                              eta_date_previous: originalDate ?? null,
                                            },
                                          ]).then(() => {
                                            // Reset editing state after successful update
                                            setSalesOrderLines(prevLines =>
                                              prevLines.map(l =>
                                                l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                                  ? {
                                                      ...l,
                                                      eta_editing: false,
                                                      originalEtaDate: undefined,
                                                      eta_date_current: currentDate,
                                                      eta_date_previous: originalDate ?? null,
                                                    }
                                                  : l,
                                              ),
                                            )
                                          })
                                        } else {
                                          // Just reset editing state if no change
                                          setSalesOrderLines(prevLines =>
                                            prevLines.map(l =>
                                              l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                                ? { ...l, eta_editing: false, originalEtaDate: undefined }
                                                : l,
                                            ),
                                          )
                                        }
                                      }}
                                    >
                                      Save
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="body2" color="text.primary">
                                      New ETA Date:{' '}
                                      {line.eta_date_current
                                        ? dayjs.utc(line.eta_date_current).format('MM/DD/YYYY')
                                        : '-'}
                                    </Typography>
                                    <Button
                                      onClick={e => {
                                        e.preventDefault()
                                        setSalesOrderLines(prevLines =>
                                          prevLines.map(l =>
                                            l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                              ? { ...l, eta_editing: true, originalEtaDate: l.eta_date_current }
                                              : l,
                                          ),
                                        )
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                            <Divider />
                            <Typography variant="body2" color="text.primary">
                              Old ETA Date:{' '}
                              {line.eta_date_previous ? dayjs.utc(line.eta_date_previous).format('MM/DD/YYYY') : '-'}
                            </Typography>
                            {user.user_type !== 'Office Staff' && (
                              <Button
                                onClick={e => {
                                  e.preventDefault()
                                  editLines([
                                    {
                                      sales_order_txn_id: salesOrder?.sales_order_txn_id ?? '',
                                      sales_order_line_txn_id: line.sales_order_line_txn_id,
                                      eta_date_previous: null,
                                      eta_date_current: null,
                                    },
                                  ]).then(() => {
                                    setSalesOrderLines(prevLines =>
                                      prevLines.map(l =>
                                        l.sales_order_line_txn_id === line.sales_order_line_txn_id
                                          ? {
                                              ...l,
                                              eta_editing: false,
                                              originalEtaDate: undefined,
                                              eta_date_previous: null,
                                              eta_date_current: null,
                                            }
                                          : l,
                                      ),
                                    )
                                  })
                                }}
                              >
                                Clear All
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.manuallyClosed}>
                          {line.is_manually_closed ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.lastModified}>
                          {line.last_update_date ? dayjs.utc(line.last_update_date).format('MM/DD/YYYY') : '-'}
                        </TableCell>
                        <TableCell width={CONDENSED_COLUMN_WIDTHS.complete}>
                          <Tooltip
                            title={
                              line.hide_flag
                                ? user.user_type === 'Administrator' || user.user_type === 'Operation Staff'
                                  ? 'Mark as not completed'
                                  : 'Completed'
                                : user.user_type === 'Administrator' || user.user_type === 'Operation Staff'
                                ? 'Mark as completed'
                                : 'Not completed'
                            }
                            placement="left"
                          >
                            <IconButton
                              onClick={
                                user.user_type === 'Administrator' || user.user_type === 'Operation Staff'
                                  ? () => {
                                      editLines([
                                        {
                                          sales_order_txn_id: salesOrder?.sales_order_txn_id ?? '',
                                          sales_order_line_txn_id: line.sales_order_line_txn_id,
                                          hide_flag: !line.hide_flag,
                                        },
                                      ])
                                    }
                                  : undefined
                              }
                            >
                              {line.hide_flag ? <CheckCircle color="success" /> : <CheckCircleOutline />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
          <Table color="primary" className="sales-order-detail-linked-txns">
            <TableHead className="sales-order-detail-table-header">
              <TableRow>
                <TableCell>{!salesOrder ? <Skeleton width={60} /> : 'Type'}</TableCell>
                <TableCell align="right">{!salesOrder ? <Skeleton width={100} /> : 'Ref. Number'}</TableCell>
                <TableCell>{!salesOrder ? <Skeleton width={120} /> : 'Vendor Name'}</TableCell>
                <TableCell align="right">{!salesOrder ? <Skeleton width={140} /> : 'Transaction Date'}</TableCell>
                <TableCell align="right">{!salesOrder ? <Skeleton width={100} /> : 'Due Date'}</TableCell>
                <TableCell align="right">{!salesOrder ? <Skeleton width={80} /> : 'Amount'}</TableCell>
                <TableCell>{!salesOrder ? <Skeleton width={80} /> : 'Status'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseOrders.map(purchaseOrder => (
                <TableRow key={purchaseOrder.ref_number}>
                  <TableCell>Purchase Order</TableCell>
                  <TableCell align="right">
                    {purchaseOrder.ref_number ? (
                      <LinkMui
                        to={`/vendor/purchaseorder/${purchaseOrder.purchase_order_txn_id}`}
                        component={LinkRouter}
                      >
                        {purchaseOrder.ref_number}
                      </LinkMui>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{purchaseOrder.name}</TableCell>
                  <TableCell align="right">{dayjs.utc(purchaseOrder.txn_date).format('MM/DD/YYYY')}</TableCell>
                  <TableCell align="right">{dayjs.utc(purchaseOrder.due_date).format('MM/DD/YYYY')}</TableCell>
                  <TableCell align="right">
                    {purchaseOrder.total_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
              ))}
              {invoices.map(invoice => (
                <TableRow key={invoice.ref_number}>
                  <TableCell>Invoice</TableCell>
                  <TableCell align="right">
                    <LinkMui to={`/customer/invoice/${invoice.invoice_txn_id}`} component={LinkRouter}>
                      {invoice.ref_number ?? '-'}
                    </LinkMui>
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">
                    {invoice.txn_date ? dayjs.utc(invoice.txn_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {invoice.due_date ? dayjs.utc(invoice.due_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {((invoice.balance_amount ?? 0) - (invoice.applied_amount ?? 0)).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TableCell>
                  <TableCell>{invoice.is_paid ? 'Paid' : 'Unpaid'}</TableCell>
                </TableRow>
              ))}
              {customerCredits.map(customerCredit => (
                <TableRow key={customerCredit.ref_number}>
                  <TableCell>Customer Credit</TableCell>
                  <TableCell align="right">
                    <LinkMui to={`/customer/credit/${customerCredit.customer_credit_txn_id}`} component={LinkRouter}>
                      {customerCredit.ref_number ?? '-'}
                    </LinkMui>
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">
                    {customerCredit.txn_date ? dayjs.utc(customerCredit.txn_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {customerCredit.due_date ? dayjs.utc(customerCredit.due_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {customerCredit.total_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ??
                      '-'}
                  </TableCell>
                  <TableCell>{customerCredit.is_paid ? 'Paid' : 'Unpaid'}</TableCell>
                </TableRow>
              ))}
              {receivePayments.map(receivePayment => (
                <TableRow key={receivePayment.receive_payment_txn_id}>
                  <TableCell>Payments</TableCell>
                  <TableCell align="right">
                    <LinkMui to={`/customer/payment/${receivePayment.receive_payment_txn_id}`} component={LinkRouter}>
                      {receivePayment.ref_number ?? '-'}
                    </LinkMui>
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">
                    {receivePayment.txn_date ? dayjs.utc(receivePayment.txn_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">
                    {receivePayment.total_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ??
                      '-'}
                  </TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rmas.length > 0 && (
            <Table color="primary" className="sales-order-detail-linked-txns">
              <TableHead className="sales-order-detail-table-header">
                <TableRow>
                  <TableCell>{!salesOrder ? <Skeleton width={60} /> : 'Type'}</TableCell>
                  <TableCell align="right">{!salesOrder ? <Skeleton width={100} /> : 'RMA ID'}</TableCell>
                  <TableCell align="right">{!salesOrder ? <Skeleton width={140} /> : 'RMA Initiation Date'}</TableCell>
                  <TableCell>{!salesOrder ? <Skeleton width={160} /> : 'RMA Customer Reason'}</TableCell>
                  <TableCell>{!salesOrder ? <Skeleton width={100} /> : 'RMA Status'}</TableCell>
                  <TableCell align="right">{!salesOrder ? <Skeleton width={120} /> : 'Last Modified'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!salesOrder
                  ? Array.from({ length: 2 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={200} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                      </TableRow>
                    ))
                  : rmas.map(rma => (
                      <TableRow key={rma.rma_id}>
                        <TableCell>RMA</TableCell>
                        <TableCell align="right">
                          <LinkMui to={`/rma/${rma.rma_id}`} component={LinkRouter}>
                            {rma.rma_number}
                          </LinkMui>
                        </TableCell>
                        <TableCell align="right">{dayjs.utc(rma.rma_initiation_date).format('MM/DD/YYYY')}</TableCell>
                        <TableCell>{rma.rma_customer_reason ?? '-'}</TableCell>
                        <TableCell>{rma.rma_status}</TableCell>
                        <TableCell align="right">
                          {rma.last_update_date ? dayjs.utc(rma.last_update_date).format('MM/DD/YYYY') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <Drawer open={serialNumbersOpen} onClose={() => setSerialNumbersOpen(false)} anchor="right">
          <SerialNumbers
            order={{
              customerName: salesOrder?.parent_name ?? '',
              jobName: salesOrder?.customer_name ?? '',
              itemName: itemSelected.name,
              salesOrderNumber: salesOrder?.ref_number ?? '',
            }}
            serialNumbers={serialNumbers.filter(sn => sn.sales_order_line_txn_id === itemSelected.id)}
            orderId={salesOrder?.sales_order_txn_id ?? ''}
            lineId={itemSelected.id}
            userType={user.user_type}
            userId={user.user_id}
            setOpen={setSerialNumbersOpen}
            setParentSerialNumbers={setSerialNumbers}
          />
        </Drawer>
        <Drawer open={trackingNumbersOpen} onClose={() => setTrackingNumbersOpen(false)} anchor="right">
          <TrackingNumbers
            order={{
              customerName: salesOrder?.parent_name ?? '',
              jobName: salesOrder?.customer_name ?? '',
              itemName: itemSelected.name,
              salesOrderNumber: salesOrder?.ref_number ?? '',
              salesOrderId: salesOrder?.sales_order_txn_id ?? '',
            }}
            trackingNumbers={trackingNumbers.filter(t => t.sales_order_line_txn_id === itemSelected.id)}
            orderId={salesOrder?.sales_order_txn_id ?? ''}
            lineId={itemSelected.id}
            userType={user.user_type}
            userId={user.user_id}
            setOpen={setTrackingNumbersOpen}
            setParentTrackingNumbers={setTrackingNumbers}
            handleAddTrackingNumber={handleAddTrackingNumber}
            handleEditTrackingNumbers={handleEditTrackingNumbers}
            onSalesOrderLinesUpdate={handleSalesOrderLinesUpdate}
          />
        </Drawer>
        <Drawer
          open={editLinesOpen}
          onClose={() => setEditLinesOpen(false)}
          anchor="right"
          variant="temporary"
          elevation={16}
          hideBackdrop
          disableScrollLock={true}
        >
          <EditLines
            lines={selectedLines}
            trackingNumbers={trackingNumbers.filter(t =>
              selectedLines.some(l => l.sales_order_line_txn_id === t.sales_order_line_txn_id),
            )}
            setParentTrackingNumbers={setTrackingNumbers}
            updateLines={editLines}
            setOpen={setEditLinesOpen}
            handleAddTrackingNumber={handleAddTrackingNumber}
            handleEditTrackingNumbers={handleEditTrackingNumbers}
          />
        </Drawer>
      </div>
      {exportError && (
        <Snackbar open={!!exportError} onClose={() => setExportError(false)}>
          <Alert severity="error">
            <AlertTitle>Export Failed</AlertTitle>
            An error occurred. Please try again or contact support if the error persists.
          </Alert>
        </Snackbar>
      )}
    </div>
  )
}

export default SalesOrder
